import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Container,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import '../Styles/product.css';
import { useNavigate } from 'react-router-dom';
import { FaClipboardList, FaEdit, FaDownload, FaFolderOpen, FaTimes, FaGripVertical } from 'react-icons/fa';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import jsQR from 'jsqr';
import {
  getAllCourses,
  getContentByCode,
  generateQRByNumber,
  getAllCourseDetails,
  getAllSubjectDetails,
  getContentForUrlMigration,
  migrateContentUrl,
  downloadContentUrlExcel,
} from '../Servies/AdmiServices';
import TableData from '../components/common/TableData';

const QR_BASE_URL = 'https://img-qrcode.alrn.in/qr-images/';
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.tif'];

export default function Courses() {
  const headers = ['Board Name', 'Course', 'Display Name', 'Status', 'Action'];
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState([]);

  // --- Get Content by Code dialog ---
  const [openContentDialog, setOpenContentDialog] = useState(false);
  const [hashedStr, setHashedStr] = useState('');
  const [contentResult, setContentResult] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState('');
  const [qrDecoding, setQrDecoding] = useState(false);
  const qrFileInputRef = useRef(null);

  // --- Generate QR by Number dialog ---
  const [openGenQrDialog, setOpenGenQrDialog] = useState(false);
  const [genQrNumber, setGenQrNumber] = useState('');
  const [genQrLoading, setGenQrLoading] = useState(false);
  const [genQrError, setGenQrError] = useState('');

  // --- QR Images dialog ---
  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [qrDirs, setQrDirs] = useState([]);
  const [qrDirsLoading, setQrDirsLoading] = useState(false);
  const [qrDirsError, setQrDirsError] = useState('');
  const [downloadingDir, setDownloadingDir] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  // --- Linking Content (URL migration) ---
  const [linkCourses, setLinkCourses] = useState([]);
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [exportContentCodes, setExportContentCodes] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportExcelError, setExportExcelError] = useState('');

  // Source course/subject — the content whose file URLs we copy FROM.
  const [srcCourseId, setSrcCourseId] = useState('');
  const [srcSubjectId, setSrcSubjectId] = useState('');
  const [srcSubjects, setSrcSubjects] = useState([]);
  const [srcSubjectsLoading, setSrcSubjectsLoading] = useState(false);
  const [srcContentList, setSrcContentList] = useState([]);
  const [srcLoading, setSrcLoading] = useState(false);
  const [srcError, setSrcError] = useState('');
  const [srcCodeFilter, setSrcCodeFilter] = useState('');

  // Destination course/subject — the content whose file URLs we copy TO.
  const [destCourseId, setDestCourseId] = useState('');
  const [destSubjectId, setDestSubjectId] = useState('');
  const [destSubjects, setDestSubjects] = useState([]);
  const [destSubjectsLoading, setDestSubjectsLoading] = useState(false);
  const [destContentList, setDestContentList] = useState([]);
  const [destLoading, setDestLoading] = useState(false);
  const [destError, setDestError] = useState('');
  const [destCodeFilter, setDestCodeFilter] = useState('');

  const [migrationResults, setMigrationResults] = useState({});
  const [migrating, setMigrating] = useState(false);
  const [migrationError, setMigrationError] = useState('');

  const handleOpenLinkDialog = () => setOpenLinkDialog(true);
  const handleCloseLinkDialog = () => {
    if (migrating || exportingExcel) return;
    setOpenLinkDialog(false);
  };

  useEffect(() => {
    getAllCourseDetails()
      .then((res) => setLinkCourses(res?.data || []))
      .catch(() => {});
  }, []);

  const handleSourceCourseChange = (courseId) => {
    setSrcCourseId(courseId);
    setSrcSubjectId('');
    setSrcSubjects([]);
    setSrcContentList([]);
    setSrcCodeFilter('');
    setMigrationResults({});
    setSrcError('');
    if (!courseId) return;
    setSrcSubjectsLoading(true);
    getAllSubjectDetails(courseId)
      .then((res) => setSrcSubjects(res?.data || []))
      .catch(() => {})
      .finally(() => setSrcSubjectsLoading(false));
  };

  const handleDestCourseChange = (courseId) => {
    setDestCourseId(courseId);
    setDestSubjectId('');
    setDestSubjects([]);
    setDestContentList([]);
    setDestCodeFilter('');
    setMigrationResults({});
    setDestError('');
    if (!courseId) return;
    setDestSubjectsLoading(true);
    getAllSubjectDetails(courseId)
      .then((res) => setDestSubjects(res?.data || []))
      .catch(() => {})
      .finally(() => setDestSubjectsLoading(false));
  };

  const handleLoadSourceContent = async () => {
    if (!srcCourseId || !srcSubjectId) {
      setSrcError('Please select both a course and a subject.');
      return;
    }
    setSrcLoading(true);
    setSrcError('');
    setSrcContentList([]);
    setSrcCodeFilter('');
    setMigrationResults({});
    try {
      const res = await getContentForUrlMigration(srcCourseId, srcSubjectId);
      setSrcContentList(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setSrcError('Failed to load content for the selected course/subject.');
    } finally {
      setSrcLoading(false);
    }
  };

  const handleLoadDestContent = async () => {
    if (!destCourseId || !destSubjectId) {
      setDestError('Please select both a course and a subject.');
      return;
    }
    setDestLoading(true);
    setDestError('');
    setDestContentList([]);
    setDestCodeFilter('');
    setMigrationResults({});
    try {
      const res = await getContentForUrlMigration(destCourseId, destSubjectId);
      setDestContentList(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setDestError('Failed to load content for the selected course/subject.');
    } finally {
      setDestLoading(false);
    }
  };

  const handleRemoveSrcItem = (contentId) => {
    setSrcContentList((prev) => prev.filter((item) => item.contentId !== contentId));
  };

  const handleRemoveDestItem = (contentId) => {
    setDestContentList((prev) => prev.filter((item) => item.contentId !== contentId));
  };

  // Mouse-based reordering (not native HTML5 drag-and-drop, which gets stuck inside a
  // sticky-header/scrollable table). Mouse-down on the grip handle starts the drag;
  // moving over another row swaps it into place immediately; mouse-up anywhere ends it.
  const [draggingIndex, setDraggingIndex] = useState(null);

  useEffect(() => {
    const endDrag = () => setDraggingIndex(null);
    window.addEventListener('mouseup', endDrag);
    return () => window.removeEventListener('mouseup', endDrag);
  }, []);

  const handleDestDragHandleMouseDown = (e, index) => {
    e.preventDefault();
    setDraggingIndex(index);
  };

  const handleDestRowMouseEnter = (index) => {
    if (draggingIndex === null || draggingIndex === index) return;
    setDestContentList((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(draggingIndex, 1);
      updated.splice(index, 0, moved);
      return updated;
    });
    setDraggingIndex(index);
  };

  const handleMigrateContentUrls = async () => {
    const pairCount = Math.min(srcContentList.length, destContentList.length);
    if (pairCount === 0) {
      setMigrationError('Load content on both sides — each destination row is matched to the source row in the same position.');
      return;
    }

    setMigrating(true);
    setMigrationError('');
    try {
      const destSlice = destContentList.slice(0, pairCount);
      const pairs = destSlice.map((destItem, index) => ({
        sourceContentCode: srcContentList[index].contentCode,
        targetContentCode: destItem.contentCode,
      }));
      const res = await migrateContentUrl(pairs);
      const results = Array.isArray(res?.data) ? res.data : [];
      const resultsByContentId = {};
      destSlice.forEach((item, index) => {
        resultsByContentId[item.contentId] = results[index];
      });
      setMigrationResults(resultsByContentId);
    } catch (err) {
      setMigrationError('Failed to migrate content URLs.');
    } finally {
      setMigrating(false);
    }
  };

  const handleDownloadContentUrlExcel = async () => {
    const contentCodes = exportContentCodes
      .split(/[\n,]+/)
      .map((code) => code.trim())
      .filter(Boolean)
      .join(',');

    if (!contentCodes) {
      setExportExcelError('Please enter at least one content code.');
      return;
    }

    setExportingExcel(true);
    setExportExcelError('');
    try {
      const res = await downloadContentUrlExcel(contentCodes);
      if (res.status !== 200) {
        const errorText = await res.data.text();
        setExportExcelError(errorText || 'Failed to download the Excel file.');
        return;
      }
      const disposition = res.headers?.['content-disposition'];
      const match = disposition && disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : `content_url_export_${Date.now()}.xlsx`;
      saveAs(res.data, fileName);
    } catch (err) {
      setExportExcelError('Failed to download the Excel file.');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleEditEdit = (courseId) => {
    navigate(`/dashboard/CreateCourse?courseId=${courseId}`);
  };

  const handleStructure = (courseId) => {
    navigate(`/dashboard/CourseStructure?courseId=${courseId}`);
  };

  const getCoursesList = () => {
    getAllCourses()
      .then((res) => setCourseData(res?.data))
      .catch(() => {});
  };

  useEffect(() => {
    getCoursesList();
  }, []);

  // ---- Get Content by Code ----
  const handleOpenContentDialog = () => {
    setOpenContentDialog(true);
    setHashedStr('');
    setContentResult(null);
    setContentError('');
  };

  const handleCloseContentDialog = () => setOpenContentDialog(false);

  const handleFetchContent = async (overrideStr) => {
    const strToSend = (overrideStr ?? hashedStr).trim();
    if (!strToSend) {
      setContentError('Please enter a hashed string.');
      return;
    }
    setContentLoading(true);
    setContentError('');
    setContentResult(null);
    try {
      const res = await getContentByCode(strToSend);
      if (res?.data?.success) {
        setContentResult(res.data);
      } else {
        setContentError(res?.data?.message || 'Failed to fetch content.');
      }
    } catch {
      setContentError('An error occurred while fetching content.');
    } finally {
      setContentLoading(false);
    }
  };

  // ---- Upload QR image -> decode -> extract Data field -> fetch content ----
  const handleUploadQrClick = () => {
    qrFileInputRef.current?.click();
  };

  const handleQrFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setContentError('');
    setContentResult(null);
    setQrDecoding(true);

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

        setQrDecoding(false);

        if (!qrCode) {
          setContentError('Could not read a QR code from the uploaded image.');
          return;
        }

        let parsed;
        try {
          parsed = JSON.parse(qrCode.data);
        } catch {
          setContentError('QR code did not contain valid data.');
          return;
        }

        const rawData = parsed?.Data;
        if (!rawData) {
          setContentError('QR code data is missing the expected "Data" field.');
          return;
        }

        const cleanedData = rawData.replace(/=+$/, '');
        setHashedStr(cleanedData);
        handleFetchContent(cleanedData);
      };
      img.onerror = () => {
        setQrDecoding(false);
        setContentError('Could not load the uploaded image.');
      };
      img.src = reader.result;
    };
    reader.onerror = () => {
      setQrDecoding(false);
      setContentError('Could not read the uploaded file.');
    };
    reader.readAsDataURL(file);
  };

  // ---- Generate QR by Number ----
  const handleOpenGenQrDialog = () => {
    setOpenGenQrDialog(true);
    setGenQrNumber('');
    setGenQrError('');
  };

  const handleCloseGenQrDialog = () => {
    if (genQrLoading) return;
    setOpenGenQrDialog(false);
  };

  const handleGenerateQR = async () => {
    const num = parseInt(genQrNumber, 10);
    if (!genQrNumber || Number.isNaN(num) || num < 1) {
      setGenQrError('Please enter a valid positive number.');
      return;
    }
    setGenQrLoading(true);
    setGenQrError('');
    try {
      // Step 1: trigger server-side generation
      const response = await generateQRByNumber(num);
      if (!response.ok) throw new Error(`Server error (HTTP ${response.status})`);

      // Step 2: find the latest directory created on the image server
      const dirRes = await fetch(QR_BASE_URL);
      if (!dirRes.ok) throw new Error('Could not fetch directory listing after generation');
      const dirs = await dirRes.json();
      const latest = dirs
        .filter((d) => d.type === 'directory')
        .sort((a, b) => new Date(b.mtime) - new Date(a.mtime))[0];

      if (!latest) throw new Error('No directory found after generation');

      // Step 3: download its images as zip using same logic as folder list
      await buildAndDownloadZip(latest.name, () => {});
      setOpenGenQrDialog(false);
    } catch (err) {
      setGenQrError(`Failed: ${err.message}`);
    } finally {
      setGenQrLoading(false);
    }
  };

  // ---- QR Images ----
  const handleOpenQrDialog = async () => {
    setOpenQrDialog(true);
    setQrDirs([]);
    setQrDirsError('');
    setQrDirsLoading(true);
    try {
      const res = await fetch(QR_BASE_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const sorted = data
        .filter((item) => item.type === 'directory')
        .sort((a, b) => new Date(b.mtime) - new Date(a.mtime))
        .slice(0, 15);
      setQrDirs(sorted);
    } catch (err) {
      setQrDirsError(`Failed to load directories: ${err.message}`);
    } finally {
      setQrDirsLoading(false);
    }
  };

  const handleCloseQrDialog = () => {
    if (downloadingDir) return;
    setOpenQrDialog(false);
  };

  const buildAndDownloadZip = async (dirName, onProgress) => {
    const listRes = await fetch(`${QR_BASE_URL}${dirName}/`);
    if (!listRes.ok) throw new Error(`Could not list directory (HTTP ${listRes.status})`);
    const files = await listRes.json();

    const imageFiles = files.filter(
      (f) => f.type === 'file' && IMAGE_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext))
    );

    if (imageFiles.length === 0) throw new Error(`No images found in "${dirName}".`);

    onProgress({ current: 0, total: imageFiles.length });

    const zip = new JSZip();
    const folder = zip.folder(dirName);

    await Promise.all(
      imageFiles.map(async (file) => {
        const imgRes = await fetch(`${QR_BASE_URL}${dirName}/${file.name}`);
        const blob = await imgRes.blob();
        folder.file(file.name, blob);
        onProgress((prev) => ({ ...prev, current: prev.current + 1 }));
      })
    );

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `${dirName}.zip`);
  };

  const handleDownloadDirZip = async (dirName) => {
    setDownloadingDir(dirName);
    setDownloadProgress({ current: 0, total: 0 });
    try {
      await buildAndDownloadZip(dirName, setDownloadProgress);
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloadingDir(null);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  // ---- Table columns ----
  const specificColumns = courseData.map((item) => ({
    'Board Name': item.boardName,
    Course: item.course,
    'Display Name': item.displayName,
    Status: item.status ? (
      <Button variant="contained" color="primary">Active</Button>
    ) : (
      <Button variant="contained" color="error">Inactive</Button>
    ),
    Action: (
      <div>
        <FaEdit
          style={{ cursor: 'pointer', marginRight: '10px' }}
          onClick={() => handleEditEdit(item.courseId)}
        />
        <FaClipboardList
          style={{ cursor: 'pointer', marginRight: '10px' }}
          onClick={() => handleStructure(item.courseId)}
        />
      </div>
    ),
  }));

  const content = contentResult?.content;
  const hashData = (() => {
    try {
      return contentResult?.hash_data ? JSON.parse(contentResult.hash_data) : null;
    } catch {
      return null;
    }
  })();

  const isDownloading = !!downloadingDir;

  // Filtering by content code is just a view filter — it keeps each row's original
  // index (used for the "#" column and for position-based migration pairing/dragging).
  const filteredSrcEntries = srcContentList
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item.contentCode?.toLowerCase().includes(srcCodeFilter.trim().toLowerCase()));

  const filteredDestEntries = destContentList
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item.contentCode?.toLowerCase().includes(destCodeFilter.trim().toLowerCase()));

  return (
    <>
      <Helmet>
        <title> Dashboard: Courses </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Courses
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<FaFolderOpen />} onClick={handleOpenQrDialog}>
              QR Images
            </Button>
            <Button variant="outlined" onClick={handleOpenGenQrDialog}>
              Generate QR
            </Button>
            <Button variant="outlined" onClick={handleOpenContentDialog}>
              Get Content by Code
            </Button>
            <Button variant="outlined" onClick={handleOpenLinkDialog}>
              Linking Course
            </Button>
            <Button variant="contained" onClick={() => navigate('/dashboard/CreateCourse')}>
              Create Course
            </Button>
          </Stack>
        </Stack>

        <TableData data={specificColumns} headers={headers} rowsPerPageOptions={[5, 10, 20]} />
      </Container>

      {/* ---- Linking Course (content URL migration) Dialog ---- */}
      <Dialog open={openLinkDialog} onClose={handleCloseLinkDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Linking Course</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Load a source course/subject and a destination course/subject. Each destination row is matched
            to the source row in the same position — press and hold the grip handle to drag a destination
            row up or down until it lines up with the source content, then migrate.
          </Typography>

          <Stack direction="row" spacing={3} mt={2} alignItems="flex-start">
            {/* ---- Source Course section ---- */}
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Source Course
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2}>
                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                  <InputLabel id="src-course-label">Course</InputLabel>
                  <Select
                    labelId="src-course-label"
                    label="Course"
                    value={srcCourseId}
                    onChange={(e) => handleSourceCourseChange(e.target.value)}
                  >
                    {linkCourses.map((course) => (
                      <MenuItem key={course.courseId} value={course.courseId}>
                        {course.courseDisplayName || course.courseId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }} disabled={!srcCourseId || srcSubjectsLoading}>
                  <InputLabel id="src-subject-label">Subject</InputLabel>
                  <Select
                    labelId="src-subject-label"
                    label="Subject"
                    value={srcSubjectId}
                    onChange={(e) => setSrcSubjectId(e.target.value)}
                  >
                    {srcSubjects.map((subject) => (
                      <MenuItem key={subject.levelId} value={subject.levelId}>
                        {subject.levelDisplayName || subject.levelId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1.5 }}
                onClick={handleLoadSourceContent}
                disabled={!srcCourseId || !srcSubjectId || srcLoading}
              >
                {srcLoading ? <CircularProgress size={18} color="inherit" /> : 'Load Content'}
              </Button>

              {srcError && (
                <Typography color="error" variant="body2" mt={1.5}>
                  {srcError}
                </Typography>
              )}

              {srcContentList.length > 0 && (
                <>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Filter by content code..."
                    value={srcCodeFilter}
                    onChange={(e) => setSrcCodeFilter(e.target.value)}
                    sx={{ mt: 2 }}
                  />
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell width={36}><strong>#</strong></TableCell>
                          <TableCell><strong>Content Code</strong></TableCell>
                          <TableCell><strong>Display Name</strong></TableCell>
                          <TableCell><strong>File URL</strong></TableCell>
                          <TableCell width={36} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSrcEntries.map(({ item, idx }) => (
                          <TableRow key={item.contentId} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{item.contentCode}</TableCell>
                            <TableCell>{item.contentDisplayName}</TableCell>
                            <TableCell>
                              {item.fileUrl ? (
                                <Typography
                                  variant="caption"
                                  sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                                >
                                  {item.fileUrl}
                                </Typography>
                              ) : (
                                <Chip label="No URL" size="small" color="default" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Remove from linking">
                                <IconButton size="small" onClick={() => handleRemoveSrcItem(item.contentId)}>
                                  <FaTimes size={14} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredSrcEntries.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary" align="center">
                                No content codes match "{srcCodeFilter}".
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>

            {/* ---- Destination Course section ---- */}
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Destination Course
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2}>
                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                  <InputLabel id="dest-course-label">Course</InputLabel>
                  <Select
                    labelId="dest-course-label"
                    label="Course"
                    value={destCourseId}
                    onChange={(e) => handleDestCourseChange(e.target.value)}
                  >
                    {linkCourses.map((course) => (
                      <MenuItem key={course.courseId} value={course.courseId}>
                        {course.courseDisplayName || course.courseId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }} disabled={!destCourseId || destSubjectsLoading}>
                  <InputLabel id="dest-subject-label">Subject</InputLabel>
                  <Select
                    labelId="dest-subject-label"
                    label="Subject"
                    value={destSubjectId}
                    onChange={(e) => setDestSubjectId(e.target.value)}
                  >
                    {destSubjects.map((subject) => (
                      <MenuItem key={subject.levelId} value={subject.levelId}>
                        {subject.levelDisplayName || subject.levelId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1.5 }}
                onClick={handleLoadDestContent}
                disabled={!destCourseId || !destSubjectId || destLoading}
              >
                {destLoading ? <CircularProgress size={18} color="inherit" /> : 'Load Content'}
              </Button>

              {destError && (
                <Typography color="error" variant="body2" mt={1.5}>
                  {destError}
                </Typography>
              )}

              {destContentList.length > 0 && (
                <>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Filter by content code..."
                    value={destCodeFilter}
                    onChange={(e) => setDestCodeFilter(e.target.value)}
                    sx={{ mt: 2 }}
                  />
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell width={36} />
                          <TableCell width={36}><strong>#</strong></TableCell>
                          <TableCell><strong>Content Code</strong></TableCell>
                          <TableCell><strong>Display Name</strong></TableCell>
                          <TableCell width={36} />
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ userSelect: draggingIndex !== null ? 'none' : 'auto' }}>
                        {filteredDestEntries.map(({ item, idx }) => {
                          const result = migrationResults[item.contentId];
                          return (
                            <TableRow
                              key={item.contentId}
                              hover
                              onMouseEnter={() => handleDestRowMouseEnter(idx)}
                              sx={{ bgcolor: draggingIndex === idx ? 'action.selected' : undefined }}
                            >
                              <TableCell
                                onMouseDown={(e) => handleDestDragHandleMouseDown(e, idx)}
                                sx={{ cursor: draggingIndex === idx ? 'grabbing' : 'grab' }}
                              >
                                <FaGripVertical color="#9e9e9e" />
                              </TableCell>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>
                                {item.contentCode}
                                {result && (
                                  <Tooltip title={result.message}>
                                    <Chip
                                      label={result.success ? 'OK' : 'Failed'}
                                      size="small"
                                      color={result.success ? 'success' : 'error'}
                                      sx={{ ml: 1 }}
                                    />
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell>{item.contentDisplayName}</TableCell>
                              <TableCell>
                                <Tooltip title="Remove from linking">
                                  <IconButton size="small" onClick={() => handleRemoveDestItem(item.contentId)}>
                                    <FaTimes size={14} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {filteredDestEntries.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary" align="center">
                                No content codes match "{destCodeFilter}".
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          </Stack>

          {(srcContentList.length > 0 || destContentList.length > 0) && (
            <>
              {srcContentList.length !== destContentList.length && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                  Source has {srcContentList.length} item(s), destination has {destContentList.length} item(s) —
                  only the first {Math.min(srcContentList.length, destContentList.length)} rows (by position) will be migrated.
                </Typography>
              )}

              {migrationError && (
                <Typography color="error" variant="body2" mt={1.5}>
                  {migrationError}
                </Typography>
              )}

              <Stack direction="row" justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  onClick={handleMigrateContentUrls}
                  disabled={migrating || srcContentList.length === 0 || destContentList.length === 0}
                >
                  {migrating ? <CircularProgress size={20} color="inherit" /> : 'Migrate Selected'}
                </Button>
              </Stack>
            </>
          )}

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom>
            Download Content URLs as Excel
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Paste content codes (comma or newline separated) to export their file URLs to an Excel file.
          </Typography>

          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder={'e.g. CRS001SUB01CH01L01, CRS001SUB01CH01L02, CRS001SUB01CH02L01'}
            value={exportContentCodes}
            onChange={(e) => setExportContentCodes(e.target.value)}
            sx={{ mt: 2 }}
          />

          {exportExcelError && (
            <Typography color="error" variant="body2" mt={2}>
              {exportExcelError}
            </Typography>
          )}

          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              startIcon={!exportingExcel ? <FaDownload /> : null}
              onClick={handleDownloadContentUrlExcel}
              disabled={exportingExcel}
            >
              {exportingExcel ? <CircularProgress size={20} color="inherit" /> : 'Download Excel'}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog} disabled={migrating || exportingExcel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Generate QR by Number Dialog ---- */}
      <Dialog open={openGenQrDialog} onClose={handleCloseGenQrDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Generate QR Codes by Number</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Number"
              type="number"
              value={genQrNumber}
              onChange={(e) => setGenQrNumber(e.target.value)}
              fullWidth
              inputProps={{ min: 1 }}
              placeholder="e.g. 3"
              error={!!genQrError}
              helperText={genQrError}
              disabled={genQrLoading}
            />
            {genQrLoading && (
              <Stack alignItems="center" py={1}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Generating QR codes...
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenQrDialog} disabled={genQrLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerateQR} disabled={genQrLoading}>
            Generate & Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- QR Images Dialog ---- */}
      <Dialog open={openQrDialog} onClose={handleCloseQrDialog} maxWidth="md" fullWidth>
        <DialogTitle>QR Image Directories</DialogTitle>
        <DialogContent>
          {qrDirsLoading && (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={30} />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Loading directories...
              </Typography>
            </Stack>
          )}

          {qrDirsError && (
            <Typography color="error" variant="body2" mt={1}>
              {qrDirsError}
            </Typography>
          )}

          {!qrDirsLoading && !qrDirsError && qrDirs.length === 0 && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              No directories found.
            </Typography>
          )}

          {qrDirs.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Directory Name</strong></TableCell>
                    <TableCell><strong>Modified Date</strong></TableCell>
                    <TableCell align="center"><strong>Download</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qrDirs.map((dir) => {
                    const isThisDownloading = downloadingDir === dir.name;
                    return (
                      <TableRow key={dir.name} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <FaFolderOpen color="#f5a623" />
                            <Typography variant="body2">{dir.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(dir.mtime).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {isThisDownloading ? (
                            <Stack alignItems="center" spacing={0.5}>
                              <CircularProgress size={18} />
                              {downloadProgress.total > 0 && (
                                <>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(downloadProgress.current / downloadProgress.total) * 100}
                                    sx={{ width: 80 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {downloadProgress.current}/{downloadProgress.total}
                                  </Typography>
                                </>
                              )}
                            </Stack>
                          ) : (
                            <Tooltip title={`Download ${dir.name}.zip`}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownloadDirZip(dir.name)}
                                  disabled={isDownloading}
                                >
                                  <FaDownload />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQrDialog} disabled={isDownloading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Get Content by Code Dialog ---- */}
      <Dialog open={openContentDialog} onClose={handleCloseContentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Get Content by Hashed Code</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Hashed String"
              value={hashedStr}
              onChange={(e) => setHashedStr(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Paste the hashed string here, or upload a QR code image..."
              error={!!contentError && !contentLoading && !qrDecoding}
              helperText={!contentLoading && !qrDecoding && contentError}
            />

            <input
              type="file"
              accept="image/*"
              ref={qrFileInputRef}
              style={{ display: 'none' }}
              onChange={handleQrFileChange}
            />
            <Button
              variant="outlined"
              onClick={handleUploadQrClick}
              disabled={qrDecoding || contentLoading}
              startIcon={qrDecoding ? <CircularProgress size={16} /> : null}
            >
              {qrDecoding ? 'Reading QR Code...' : 'Upload QR Code'}
            </Button>

            {contentLoading && (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={28} />
              </Stack>
            )}

            {contentResult && content && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                {hashData && (
                  <>
                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                      {Object.entries(hashData).map(([k, v]) => (
                        <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" />
                      ))}
                    </Stack>
                    <Divider sx={{ mb: 1.5 }} />
                  </>
                )}

                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {content.contentName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {content.contentDescription}
                </Typography>

                <Stack spacing={0.5} mt={1}>
                  <InfoRow label="Content Code" value={content.contentCode} />
                  <InfoRow label="Display Name" value={content.contentDisplayName} />
                  <InfoRow
                    label="Type"
                    value={content.contentTypeCode === 1 ? 'AV (Audio/Video)' : content.contentTypeCode}
                  />
                  <InfoRow label="Sequence No" value={content.sequenceNo} />
                  <InfoRow label="Duration" value={`${content.duration}s`} />
                  <InfoRow
                    label="Status"
                    value={
                      <Chip
                        label={content.isActive ? 'Active' : 'Inactive'}
                        color={content.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                </Stack>

                {content.fileURL && (
                  <Box mt={2}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Media URL
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        bgcolor: 'grey.100',
                        p: 1,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                      }}
                    >
                      {content.fileURL}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContentDialog}>Close</Button>
          <Button variant="contained" onClick={() => handleFetchContent()} disabled={contentLoading}>
            Fetch Content
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}:
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body2">{value}</Typography>
      ) : (
        value
      )}
    </Stack>
  );
}
